import React from "react";
import MuiStack from "@mui/material/Stack";
import PropTypes from "prop-types";

/**
 * Stack component wrapper for one-dimensional layouts.
 * Wraps Material-UI Stack with consistent spacing and direction control.
 * 
 * @component
 * @example
 * <Stack direction="row" spacing={2}>
 *   <Button>Cancel</Button>
 *   <Button>Submit</Button>
 * </Stack>
 * 
 * @example
 * // Responsive direction
 * <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
 *   <TextField />
 *   <TextField />
 * </Stack>
 */
const Stack = React.forwardRef(({ children, ...props }, ref) => {
  return <MuiStack ref={ref} {...props}>{children}</MuiStack>;
});

Stack.displayName = 'Stack';

Stack.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * Defines the flex-direction style property
   * Can be a string or object for responsive values
   */
  direction: PropTypes.oneOfType([
    PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
    PropTypes.shape({
      xs: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
      sm: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
      md: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
      lg: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
      xl: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
    }),
  ]),
  /**
   * Defines the space between immediate children
   * Can be a number (theme spacing multiplier) or object for responsive values
   */
  spacing: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({
      xs: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sm: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      md: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      lg: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      xl: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ]),
  /**
   * Defines the align-items style property
   */
  alignItems: PropTypes.oneOfType([
    PropTypes.oneOf([
      "flex-start",
      "center",
      "flex-end",
      "stretch",
      "baseline",
    ]),
    PropTypes.object,
  ]),
  /**
   * Defines the justify-content style property
   */
  justifyContent: PropTypes.oneOfType([
    PropTypes.oneOf([
      "flex-start",
      "center",
      "flex-end",
      "space-between",
      "space-around",
      "space-evenly",
    ]),
    PropTypes.object,
  ]),
  /**
   * If true, the CSS flexbox gap is used instead of applying margin to children
   */
  useFlexGap: PropTypes.bool,
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - maintain exact MUI Stack behavior
// MUI Stack defaults: direction="row", spacing=0

export default Stack;

